import './property';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { isEmpty, isDefined } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logSidebar.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-sidebar' )
export class AutomatorLogSidebar extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties

	// The title displayed on the header, next to the "X" that closes the sidebar
	@property( { type: String, attribute: false } ) accessor title = strings.details;

	// The summary of the data that's being displayed
	// {
	//     "title": String or TemplateResult,
	//     "items": [
	//         {
	//             "label": String,
	//             "value": String or TemplateResult,
	//         },
	//     ]
	// }
	@property( { type: Object, attribute: false } ) accessor summary;

	// Data about each run/try/number of times
	// {
	//     title: 'Try 2/2',
	//     summary: [
	//         {
	//             label: 'Status',
	//             value: String/TemplateResult
	//         }
	//     ]
	//     properties: [
	//         {
	//             type: 'url',
	//             label: 'URL',
	//             value: 'https://domain.com/wp-json/uap/v2/uap-10586-10588'
	//         },
	//         {
	//             type: 'email',
	//             label: 'Email address',
	//             value: { raw: '{{user_email}}', parsed: 'agustin@uncannyowl.com' }
	//         },
	//     ]
	// },
	// {
	//     title: 'Try 1/2',
	//     properties: [
	//         ...
	//     ]
	// }
	@property( { type: Array, attribute: false } ) accessor runs = [];

	// Miscellaneous data. This is used, for example, to set `.runsAlwaysShowSummaries`
	@property( { type: Object, attribute: false } ) accessor miscellaneous = {};

	// eslint-disable-next-line require-jsdoc
	render() {
		// Get data or show placeholder
		return html`
			<div class="sidebar">
				${ this._templateHeader }

				<div class="sidebar-content">
					${ this._templateSummary }
					${ this._templateRuns }
				</div>
			</div>
		`;
	}

	/**
	 * Request the parent to close the sidebar and remove the existing information
	 */
	requestCloseSidebar() {
		// Close it
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-sidebar', {
				bubbles: true,
				composed: true,
				detail: {
					action: 'close',
				}
			} )
		);

		return;
	}

	/**
	 * Requests the dialog to close itself
	 */
	requestCloseDialog() {
		this.dispatchEvent(
			new CustomEvent(
				'uap-log-request-dialog-close',
				{
					bubbles: true,
					composed: true
				}
			)
		);

		return;
	}

	/**
	 * Returns the template of the header of the sidebar
	 *
	 * @return {TemplateResult} The template of the header
	 */
	get _templateHeader() {
		return html`
			<div class="sidebar-header">
				<uo-tooltip
					class="sidebar-header-close-sidebar"
				>
					${ strings.closeSidebar }

					<uo-button 
						slot="target"
						color="transparent"
						size="small"
						@click=${ this.requestCloseSidebar }
					>
						<uo-icon id="angle-left"></uo-icon>
					</uo-button>
				</uo-tooltip>

				<div class="sidebar-header-title">
					${ this.title }
				</div>

				<uo-tooltip
					class="sidebar-header-close-dialog"
				>
					${ strings.closeDialog }

					<uo-button 
						slot="target"
						color="transparent"
						size="small"
						@click=${ this.requestCloseDialog }
					>
						<uo-icon id="times"></uo-icon>
					</uo-button>
				</uo-tooltip>
				
			</div>
		`;
	}

	/**
	 * Returns the template of the summary of the sidebar
	 * This includes a title that summarizes the content below and a table with miscellaneous data.
	 *
	 * @return {TemplateResult} The template of the summary
	 */
	get _templateSummary() {
		return html`
			<div class="sidebar-summary">
				${
					! isEmpty( this.summary.title ) ?
					html`
						<div class="sidebar-summary-title">
							${ this.summary.title }
						</div>
					` :
					''
				}

				${
					! isEmpty( this.summary.items ) ?
					html`
						<table class="sidebar-summary-items">
							<tbody>
								${
									repeat(
										this.summary.items,
										( item, index ) => index,
										item => {
											return html`
												<tr>
													<td class="item__key">
														${ item.label }
													</td>
													<td class="item__value">
														${ item.value }
													</td>
												</tr>
											`;
										}
									)
								}
							</tbody>
						</table>
					`:
					''
				}
			</div>
		`;
	}

	/**
	 * Renders the trigger/action/condition runs/number of times/tries/instances
	 *
	 * @return {TemplateResult} The template of the runs
	 */
	get _templateRuns() {
		// Check if there are runs
		if ( this.runs.length === 0 ) {
			return [];
		}

		// Check if there is only one run and if the dev doesn't want to show the summary anyway
		if ( this.runs.length === 1 && ! this.runsAlwaysShowSummaries ) {
			return this._templateRunsOneRun;
		}

		return this._templateRunsMultipleRuns;
	}

	/**
	 * The template used when there is one run
	 *
	 * @return {TemplateResult} The template of the runs when there is only one
	 */
	get _templateRunsOneRun() {
		// Get the data of that single run
		const run = this.runs[0];

		return html`
			<div class="sidebar-runs">
				<div class="sidebar-runs__run">
					${
						! isEmpty( run.properties ) ?
						html `
							<div class="sidebar-runs__run-properties">
								${
									repeat(
										run.properties,
										( property, index ) => index,
										property => html`
											<uap-log-sidebar-property
												.type="${ property.type }"
												.label="${ property.label }"
												.value="${ property.value }"
												.attributes="${ property.attributes }"
											></uap-log-sidebar-property>
										`
									)
								}
							</div>
						` :
						''
					}
				</div>
			</div>
		`;
	}

	/**
	 * The template used when there are multiple runs
	 *
	 * @return {TemplateResult} The template of the runs when there are multiple
	 */
	get _templateRunsMultipleRuns() {
		return html`
			<div class="sidebar-runs">
				${
					repeat(
						// Show last run first
						this.runs.reverse(),
						( run, index ) => index,
						( run, index ) => html`
							<details
								?open=${ index === 0 /* First open by default */ }
								class="sidebar-runs__run"
							>
								<summary>
									<div class="sidebar-runs__run-title">
										${ run.title }
									</div>
									
									<uo-icon id="angle-down"></uo-icon>
								</summary>

								${
									! isEmpty( run.summary ) ?
									html `
										<div class="sidebar-runs__run-summary">
											<table class="sidebar-runs__run-summary-table">
												<tbody>
													${
														repeat(
															run.summary,
															( item, index ) => index,
															item => {
																return html`
																	<tr>
																		<td class="item__key">
																			${ item.label }
																		</td>
																		<td class="item__value">
																			${ item.value }
																		</td>
																	</tr>
																`;
															}
														)
													}
												</tbody>
											</table>
										</div>
									` :
									''
								}

								${
									! isEmpty( run.properties ) ?
									html `
										<div class="sidebar-runs__run-properties">
											${
												repeat(
													run.properties,
													( property, index ) => index,
													property => html`
														<uap-log-sidebar-property
															.type="${ property.type }"
															.label="${ property.label }"
															.value="${ property.value }"
															.attributes="${ property.attributes }"
														></uap-log-sidebar-property>
													`
												)
											}
										</div>
									` :
									''
								}
							</details>
						`
					)
				}
			</div>
		`;
	}

	/**
	 * When there is only a single run, Automator will typically hide the run's summary and attempt to display only the properties, which usually pertains to the fields. However, for some actions, like those executed within loops, it is necessary to always display the summary of each run, regardless of whether there is only one run.
	 *
	 * @type {Boolean} - Whether to always show the run's summary
	 * @default false
	 */
	get runsAlwaysShowSummaries() {
		return isDefined( this.miscellaneous?.runsAlwaysShowSummaries ) ?
			this.miscellaneous.runsAlwaysShowSummaries :
			false;
	}
}
